import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { ErrorUtilities, TextUtilities } from '@pwc/pwc-deals-web-common';
import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';
import {
  getErrorMessageDuplicateTitle,
  TITLE_DUPLICATION_CONTROL_FAILED,
} from 'common/helpers/duplicateTitleError';
import { projectsContentsSearch } from 'features/analyses/analysesService';
import { useTranslation } from 'react-i18next';

export const MAX_SIZE_TITLE = 350;

export const validateTitle = (value: string) => {
  let error = '';
  if (!value || value.trim().length === 0) {
    error = 'Title is required';
  }
  if (value && value.length > MAX_SIZE_TITLE) {
    error = 'Number of characters exceeds the available space in the report';
  }
  return error;
};

type Props = {
  title: string;
  projectId: number;
  draftContentId: number;
  clientContentId: number;
};

const useSearchTitleDuplicity = ({
  title,
  projectId,
  draftContentId,
  clientContentId,
}: Props) => {
  const [titleError, setTitleError] = useState<string | null>(null);
  const { t } = useTranslation();
  const searchTitleDuplicity = useCallback(
    async (valueTitle: string) => {
      if (!valueTitle.length) {
        setTitleError(null);
        return false;
      }
      if (isTitleRestricted(valueTitle)) {
        setTitleError(restrictedErrorMessage);
        return false;
      }
      const trimmedTitle = TextUtilities.trimText(valueTitle) as string;
      const err = validateTitle(trimmedTitle);
      if (err.length > 0 && trimmedTitle.length) {
        setTitleError(err);
        return false;
      }

      // Rebased from AB#77677 KFA Error title blink for a while after share and edit
      if (clientContentId && !draftContentId) {
        // to ignor little gap when we shared KFA and API is creating draft - to prevent blink with error
        return true;
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        });

        if (
          response &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          response.length > 0 &&
          // Rebased Trim titles from BE to check title duplicity
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
          TextUtilities.trimText(response[0].title).toLowerCase() ===
            trimmedTitle.toLowerCase() &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          response[0].contentId !== draftContentId &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          response[0].contentId !== clientContentId
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          setTitleError(getErrorMessageDuplicateTitle(response[0], t));
          return false;
        }
        setTitleError(null);
      } catch (e) {
        setTitleError(
          ErrorUtilities.getErrorMessage(e, t(TITLE_DUPLICATION_CONTROL_FAILED))
        );
      }
      return true;
    },
    [clientContentId, draftContentId, projectId, t]
  );

  const debouncedSearchTitle = useMemo(
    () => debounce(searchTitleDuplicity, 450),
    [searchTitleDuplicity]
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    debouncedSearchTitle(title);
  }, [title, debouncedSearchTitle]);

  return { titleError };
};
export default useSearchTitleDuplicity;
